import React from 'react';
import { Box, Container, Typography, Card, Stack } from '@mui/material';
import { styled } from '@mui/material/styles';
import useMetaTags from '../../hooks/useMetaTags';
import PageHero from '../../components/PageHero/PageHero';
import Footer from '../../components/Footer/Footer';
import AgricultureIcon from '@mui/icons-material/Agriculture';
import NatureIcon from '@mui/icons-material/Nature';
import SchoolIcon from '@mui/icons-material/School';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import StarIcon from '@mui/icons-material/Star';
import GroupsIcon from '@mui/icons-material/Groups';

const ContentSection = styled(Box)(({ theme }) => ({
  padding: theme.spacing(8, 0, 12, 0),
  background: 'linear-gradient(135deg, #f8f9fa 0%, #ffffff 100%)',
  minHeight: '60vh',
}));

const HeroCard = styled(Card)(({ theme }) => ({
  padding: theme.spacing(6),
  borderRadius: theme.spacing(3),
  boxShadow: '0 12px 40px rgba(194, 184, 168, 0.15)',
  transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
  border: '1px solid rgba(194, 184, 168, 0.08)',
  background: 'rgba(255, 255, 255, 0.95)',
  backdropFilter: 'blur(10px)',
  marginBottom: theme.spacing(6),
  position: 'relative',
  overflow: 'hidden',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    height: '4px',
    background: `linear-gradient(90deg, ${theme.palette.forest.main} 0%, ${theme.palette.forest.light} 100%)`,
  },
}));

const ModernCard = styled(Card)(({ theme }) => ({
  padding: theme.spacing(6),
  borderRadius: theme.spacing(3),
  boxShadow: '0 8px 32px rgba(194, 184, 168, 0.12)',
  transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
  border: '1px solid rgba(194, 184, 168, 0.08)',
  background: 'rgba(255, 255, 255, 0.95)',
  backdropFilter: 'blur(10px)',
  '&:hover': {
    transform: 'translateY(-4px)',
    boxShadow: '0 16px 48px rgba(194, 184, 168, 0.18)',
    border: '1px solid rgba(194, 184, 168, 0.15)',
  },
}));

const IconWrapper = styled(Box)(({ theme }) => ({
  width: 64,
  height: 64,
  borderRadius: '50%',
  background: `linear-gradient(135deg, ${theme.palette.forest.main} 0%, ${theme.palette.forest.light} 100%)`,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  marginBottom: theme.spacing(3),
  boxShadow: `0 8px 24px rgba(69, 91, 64, 0.3)`,
}));

const ImageCard = styled(Box)(({ theme }) => ({
  borderRadius: theme.spacing(3),
  overflow: 'hidden',
  boxShadow: '0 12px 40px rgba(194, 184, 168, 0.20)',
  transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
  marginBottom: theme.spacing(3),
  '&:hover': {
    transform: 'scale(1.02)',
    boxShadow: '0 16px 48px rgba(194, 184, 168, 0.25)',
  },
  '& img': {
    width: '100%',
    height: '300px',
    objectFit: 'cover',
    transition: 'transform 0.4s ease',
  },
  '&:hover img': {
    transform: 'scale(1.05)',
  },
}));

const StatsCard = styled(Box)(({ theme }) => ({
  padding: theme.spacing(4),
  borderRadius: theme.spacing(3),
  background: `linear-gradient(135deg, ${theme.palette.forest.main} 0%, ${theme.palette.forest.dark} 100%)`,
  color: '#ffffff',
  textAlign: 'center',
  boxShadow: '0 12px 40px rgba(69, 91, 64, 0.25)',
  transition: 'all 0.3s ease',
  marginBottom: theme.spacing(3),
  '&:hover': {
    transform: 'translateY(-4px)',
    boxShadow: '0 16px 48px rgba(69, 91, 64, 0.30)',
  },
}));

const Badge = styled(Box)(({ theme }) => ({
  display: 'inline-flex',
  alignItems: 'center',
  padding: theme.spacing(1, 2),
  backgroundColor: 'rgba(69, 91, 64, 0.1)',
  color: theme.palette.forest.main,
  borderRadius: '20px',
  fontSize: '0.875rem',
  fontWeight: 600,
  marginRight: theme.spacing(2),
  marginBottom: theme.spacing(2),
  border: '1px solid rgba(69, 91, 64, 0.2)',
}));

const LaFinca: React.FC = () => {
  // Update meta tags for La Finca page
  useMetaTags({
    title: 'La Finca - Laboratorio Vivo de Agricultura Ecológica',
    description: 'Descubre nuestra finca de 30.000 m² en Bétera, Valencia. Un laboratorio vivo donde practicamos agricultura ecológica, permacultura y técnicas sostenibles de cultivo.',
    image: 'https://images.unsplash.com/photo-1625246333195-78d9c38ad449?q=80&w=2070',
    url: 'https://ekkofood.com/la-finca',
    type: 'website'
  });

  return (
    <>
      <PageHero 
        title="La Finca"
        subtitle="Nuestro Laboratorio Vivo"
        description="Donde la teoría se encuentra con la práctica en agricultura ecológica y sostenible"
        backgroundImage="https://images.unsplash.com/photo-1625246333195-78d9c38ad449?q=80&w=2070"
        height="500px"
        showButton={false}
        contentAlign="center"
        overlay={true}
        overlayOpacity={0.4}
        showBreadcrumbs={true}
        breadcrumbs={[
          { label: "Inicio", link: "/" },
          
          { label: "La Finca", link: "/la-finca" }
        ]}
      />
      
      <ContentSection>
        <Container maxWidth="lg">
          {/* Introduction Section */}
          <HeroCard>
            <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 4, alignItems: 'center' }}>
              <Box sx={{ flex: 1 }}>
                <Typography 
                  variant="h3" 
                  gutterBottom 
                  sx={{ 
                    color: 'forest.main', 
                    fontWeight: 700, 
                    mb: 3,
                    fontSize: { xs: '1.8rem', md: '2.2rem' },
                    letterSpacing: '-0.02em'
                  }}
                >
                  El Corazón de Ekkofood
                </Typography>
                <Typography 
                  variant="body1" 
                  paragraph 
                  sx={{ 
                    lineHeight: 1.7, 
                    color: 'text.primary', 
                    fontSize: '1.1rem',
                    mb: 3
                  }}
                >
                  Nuestra finca es el <strong>laboratorio vivo de Ekkofood</strong>, un espacio donde la teoría se encuentra con la práctica. 
                  Aquí enseñamos y aplicamos técnicas de agricultura orgánica, regenerativa y sostenible que permiten a los agricultores 
                  producir de manera independiente y respetuosa con el medio ambiente.
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 3 }}>
                  <Badge>
                    <StarIcon sx={{ fontSize: 16, mr: 1 }} />
                    Agricultura Ecológica desde 2000
                  </Badge>
                  <Badge>
                    <NatureIcon sx={{ fontSize: 16, mr: 1 }} />
                    Miembro Slow Farm
                  </Badge>
                </Box>
                <Typography 
                  variant="body1" 
                  sx={{ 
                    lineHeight: 1.7, 
                    color: 'text.primary',
                    fontSize: '1.1rem',
                    fontStyle: 'italic',
                    pl: 3,
                    borderLeft: '3px solid',
                    borderColor: 'forest.light',
                    backgroundColor: 'rgba(69, 91, 64, 0.05)',
                    padding: 2,
                    borderRadius: 2
                  }}
                >
                  "La finca no solo es un espacio de producción, sino también un lugar de aprendizaje, innovación y 
                  conexión con la naturaleza, donde organizamos cursos, talleres y experiencias prácticas que acercan 
                  a cada participante a la realidad de la agricultura ecológica."
                </Typography>
              </Box>
              <Box sx={{ flex: { xs: 1, md: 0.4 } }}>
                <ImageCard>
                  <img 
                    src="https://images.unsplash.com/photo-1500937386664-56d1dfef3854?q=80&w=2070" 
                    alt="Vista panorámica de nuestra finca ecológica"
                  />
                </ImageCard>
              </Box>
            </Box>
          </HeroCard>

          {/* Stats Section */}
          <Box sx={{ mb: 8 }}>
            <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 3 }}>
              <Box sx={{ flex: 1 }}>
                <StatsCard>
                  <Typography variant="h3" sx={{ fontWeight: 700, mb: 1 }}>
                    +24
                  </Typography>
                  <Typography variant="h6" sx={{ fontWeight: 500 }}>
                    Años de Experiencia
                  </Typography>
                </StatsCard>
              </Box>
              <Box sx={{ flex: 1 }}>
                <StatsCard>
                  <Typography variant="h3" sx={{ fontWeight: 700, mb: 1 }}>
                    100%
                  </Typography>
                  <Typography variant="h6" sx={{ fontWeight: 500 }}>
                    Certificación Ecológica
                  </Typography>
                </StatsCard>
              </Box>
              <Box sx={{ flex: 1 }}>
                <StatsCard>
                  <Typography variant="h3" sx={{ fontWeight: 700, mb: 1 }}>
                    500+
                  </Typography>
                  <Typography variant="h6" sx={{ fontWeight: 500 }}>
                    Alumnos Formados
                  </Typography>
                </StatsCard>
              </Box>
              <Box sx={{ flex: 1 }}>
                <StatsCard>
                  <Typography variant="h3" sx={{ fontWeight: 700, mb: 1 }}>
                    15+
                  </Typography>
                  <Typography variant="h6" sx={{ fontWeight: 500 }}>
                    Hectáreas Orgánicas
                  </Typography>
                </StatsCard>
              </Box>
            </Box>
          </Box>

          {/* Features Section */}
          <Typography 
            variant="h3" 
            align="center" 
            gutterBottom 
            sx={{ 
              color: 'forest.main', 
              fontWeight: 700, 
              mb: 6,
              fontSize: { xs: '2rem', md: '2.5rem' },
              letterSpacing: '-0.02em'
            }}
          >
            Lo Que Nos Hace Únicos
          </Typography>

          <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 4, mb: 8 }}>
            <Box sx={{ flex: 1 }}>
              <ModernCard>
                <IconWrapper>
                  <NatureIcon sx={{ fontSize: 32, color: 'white' }} />
                </IconWrapper>
                <Typography 
                  variant="h3" 
                  gutterBottom 
                  sx={{ 
                    color: 'forest.main', 
                    fontWeight: 700, 
                    mb: 4,
                    fontSize: { xs: '1.8rem', md: '2.2rem' },
                    letterSpacing: '-0.02em'
                  }}
                >
                  Certificación Oficial
                </Typography>
                <Typography 
                  variant="body1" 
                  paragraph 
                  sx={{ 
                    lineHeight: 1.7, 
                    color: 'text.primary', 
                    fontSize: '1.1rem',
                    mb: 3
                  }}
                >
                  Desde 2000 contamos con el <strong>sello oficial de Agricultura Ecológica</strong>, 
                  garantizando la máxima calidad y sostenibilidad en todos nuestros procesos productivos.
                </Typography>
                <Box sx={{ pt: 2, borderTop: '1px solid rgba(69, 91, 64, 0.1)', display: 'flex', alignItems: 'center' }}>
                  <CheckCircleIcon sx={{ color: 'success.main', fontSize: 16, mr: 1 }} />
                  <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                    Certificado desde 2000
                  </Typography>
                </Box>
              </ModernCard>
            </Box>
            
            <Box sx={{ flex: 1 }}>
              <ModernCard>
                <IconWrapper>
                  <AgricultureIcon sx={{ fontSize: 32, color: 'white' }} />
                </IconWrapper>
                <Typography 
                  variant="h3" 
                  gutterBottom 
                  sx={{ 
                    color: 'forest.main', 
                    fontWeight: 700, 
                    mb: 4,
                    fontSize: { xs: '1.8rem', md: '2.2rem' },
                    letterSpacing: '-0.02em'
                  }}
                >
                  Miembro Slow Farm
                </Typography>
                <Typography 
                  variant="body1" 
                  paragraph 
                  sx={{ 
                    lineHeight: 1.7, 
                    color: 'text.primary',
                    fontSize: '1.1rem',
                    mb: 3
                  }}
                >
                  Recientemente nos convertimos en <strong>finca miembro de Slow Farm</strong>, 
                  reflejando nuestro compromiso de más de dos décadas con la sostenibilidad y biodiversidad.
                </Typography>
                <Box sx={{ pt: 2, borderTop: '1px solid rgba(69, 91, 64, 0.1)', display: 'flex', alignItems: 'center' }}>
                  <CheckCircleIcon sx={{ color: 'success.main', fontSize: 16, mr: 1 }} />
                  <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                    Compromiso con la biodiversidad
                  </Typography>
                </Box>
              </ModernCard>
            </Box>
          </Box>

          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mb: 8 }}>
            <Box sx={{ maxWidth: '800px', width: '100%' }}>
              <ModernCard>
                <IconWrapper sx={{ margin: '0 auto', mb: 3 }}>
                  <SchoolIcon sx={{ fontSize: 32, color: 'white' }} />
                </IconWrapper>
                <Typography 
                  variant="h3" 
                  align="center"
                  gutterBottom 
                  sx={{ 
                    color: 'forest.main', 
                    fontWeight: 700, 
                    mb: 4,
                    fontSize: { xs: '1.8rem', md: '2.2rem' },
                    letterSpacing: '-0.02em'
                  }}
                >
                  Centro de Aprendizaje
                </Typography>
                <Typography 
                  variant="body1" 
                  align="center"
                  paragraph 
                  sx={{ 
                    lineHeight: 1.7, 
                    color: 'text.primary',
                    fontSize: '1.1rem',
                    mb: 3
                  }}
                >
                  Un lugar de <strong>aprendizaje, innovación y conexión</strong> con la naturaleza, donde organizamos 
                  cursos, talleres y experiencias prácticas para cada participante.
                </Typography>
                <Box sx={{ pt: 2, borderTop: '1px solid rgba(69, 91, 64, 0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <GroupsIcon sx={{ color: 'forest.main', fontSize: 16, mr: 1 }} />
                  <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                    Experiencias prácticas hands-on
                  </Typography>
                </Box>
              </ModernCard>
            </Box>
          </Box>


        </Container>
      </ContentSection>

      <Footer />
    </>
  );
};

export default LaFinca;